/* eslint-disable consistent-return */
import { useEffect, useRef } from 'react';
import { doc, onSnapshot, FirestoreError } from 'firebase/firestore';
import {
  capturePosthogException,
  capturePosthogMessage,
} from '../../../../shared/utils/posthog-error-reporting';
import { db } from '../../../../firebase/config';
import { useAuth } from '../../../firebase/auth';

const LEAD_FINDER_NOTIFICATIONS_COLLECTION = 'lead_finder_notifications';
const DOC_CREATION_TIMEOUT_MS = 10000;
const STALE_NOTIFICATION_MS = 30 * 60 * 1000; // 30 minutes
const RETRY_DELAY_MS = 1500;

// Error codes from Firestore where the listener has a real chance of recovering.
// We re-subscribe once on these before bubbling the error up.
const TRANSIENT_FIREBASE_ERROR_CODES = new Set<string>([
  'cancelled',
  'unavailable',
  'deadline-exceeded',
  'internal',
  'aborted',
  'unknown',
]);

export interface LeadFinderNotificationDoc {
  notificationUuId: string;
  notificationType: string;
  accessibleUserIds: string[];
  status: 'in_progress' | 'completed' | 'failed';
  totalCount: number;
  completedCount: number;
  failedCount: number;
  emailCredits: number;
  phoneCredits: number;
  personalEmailCredits: number;
  totalCredits: number;
  verifiedEmailLeads: number;
  verifiedPhoneLeads: number;
  verifiedPersonalEmailLeads: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

interface UseLeadFinderRealtimeNotificationProps {
  notificationUuIds: Set<string>;
  onProgress: (data: LeadFinderNotificationDoc) => void;
  onComplete: (data: LeadFinderNotificationDoc) => void;
  onError: (notificationUuId: string, error: Error) => void;
}

export const useLeadFinderRealtimeNotification = ({
  notificationUuIds,
  onProgress,
  onComplete,
  onError,
}: UseLeadFinderRealtimeNotificationProps): void => {
  const { uid } = useAuth();

  const onProgressRef = useRef(onProgress);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const completedUuIdsRef = useRef<Set<string>>(new Set());
  // Persist retry attempts across effect runs so we don't loop forever.
  // Cleared per-uuid the moment a real snapshot arrives.
  const docMissRetriedRef = useRef<Set<string>>(new Set());
  const transientRetriedRef = useRef<Set<string>>(new Set());

  // Keep refs in sync to avoid stale closures in listeners
  useEffect(() => {
    onProgressRef.current = onProgress;
  }, [onProgress]);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (notificationUuIds.size === 0 || !uid) {
      return;
    }

    const unsubscribes: Map<string, () => void> = new Map();
    const timeouts: Map<string, ReturnType<typeof setTimeout>> = new Map();
    const retryTimeouts: Map<string, ReturnType<typeof setTimeout>> = new Map();
    let cancelled = false;

    const subscribe = (notificationUuId: string): void => {
      if (cancelled) return;
      const unsubscribe = onSnapshot(
        doc(db, LEAD_FINDER_NOTIFICATIONS_COLLECTION, notificationUuId),
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            // Doc exists — clear any pending timeout and reset retry budgets
            const existingTimeout = timeouts.get(notificationUuId);
            if (existingTimeout) {
              clearTimeout(existingTimeout);
              timeouts.delete(notificationUuId);
            }
            docMissRetriedRef.current.delete(notificationUuId);
            transientRetriedRef.current.delete(notificationUuId);

            const newData = docSnapshot.data() as LeadFinderNotificationDoc;

            // Staleness check: in_progress but created >30 min ago with
            // zero progress → treat as stuck.
            if (
              newData.status === 'in_progress' &&
              newData.completedCount === 0 &&
              newData.createdAt
            ) {
              const age =
                Date.now() - new Date(newData.createdAt).getTime();
              if (age > STALE_NOTIFICATION_MS) {
                const unsub = unsubscribes.get(notificationUuId);
                if (unsub) {
                  unsub();
                  unsubscribes.delete(notificationUuId);
                }
                onErrorRef.current(
                  notificationUuId,
                  new Error('Notification stale — no progress for 30+ minutes'),
                );
                return;
              }
            }

            if (
              newData.status === 'completed' &&
              !completedUuIdsRef.current.has(notificationUuId)
            ) {
              completedUuIdsRef.current.add(notificationUuId);
              onCompleteRef.current(newData);

              // Auto-unsubscribe on completion
              const unsub = unsubscribes.get(notificationUuId);
              if (unsub) {
                unsub();
                unsubscribes.delete(notificationUuId);
              }
            } else if (newData.status === 'failed') {
              // Stop tracking and surface the failure so callers fall back
              // to polling instead of leaving the progress bar stuck.
              const unsub = unsubscribes.get(notificationUuId);
              if (unsub) {
                unsub();
                unsubscribes.delete(notificationUuId);
              }
              onErrorRef.current(
                notificationUuId,
                new Error('Lead finder bulk operation failed'),
              );
            } else if (newData.status !== 'completed') {
              onProgressRef.current(newData);
            }
          } else if (!timeouts.has(notificationUuId)) {
            // Doc doesn't exist yet — start a 10s grace period.
            const timeout = setTimeout(() => {
              timeouts.delete(notificationUuId);
              if (!docMissRetriedRef.current.has(notificationUuId)) {
                // First miss — re-subscribe once before declaring failure.
                docMissRetriedRef.current.add(notificationUuId);
                const oldUnsub = unsubscribes.get(notificationUuId);
                if (oldUnsub) {
                  oldUnsub();
                  unsubscribes.delete(notificationUuId);
                }
                subscribe(notificationUuId);
                return;
              }
              const error = new Error(
                'Firebase document not found within timeout',
              );
              onErrorRef.current(notificationUuId, error);

              if (process.env.NODE_ENV === 'production') {
                capturePosthogMessage(
                  'Firebase document not created within timeout for lead finder notification',
                  {
                    tags: { firebase: 'lead-finder-doc-timeout' },
                    extra: { notificationUuId },
                  },
                );
              }
            }, DOC_CREATION_TIMEOUT_MS);
            timeouts.set(notificationUuId, timeout);
          }
        },
        (err: FirestoreError) => {
          const existingTimeout = timeouts.get(notificationUuId);
          if (existingTimeout) {
            clearTimeout(existingTimeout);
            timeouts.delete(notificationUuId);
          }

          const isTransient = TRANSIENT_FIREBASE_ERROR_CODES.has(err?.code);
          if (
            isTransient &&
            !transientRetriedRef.current.has(notificationUuId)
          ) {
            // Transient — re-subscribe once after a short delay.
            transientRetriedRef.current.add(notificationUuId);
            const oldUnsub = unsubscribes.get(notificationUuId);
            if (oldUnsub) {
              oldUnsub();
              unsubscribes.delete(notificationUuId);
            }
            const retryTimeout = setTimeout(() => {
              retryTimeouts.delete(notificationUuId);
              subscribe(notificationUuId);
            }, RETRY_DELAY_MS);
            retryTimeouts.set(notificationUuId, retryTimeout);
            return;
          }

          onErrorRef.current(notificationUuId, err);

          if (process.env.NODE_ENV === 'production') {
            capturePosthogException(err, {
              tags: { firebase: 'lead-finder-listener-error' },
              extra: {
                notificationUuId,
                errorCode: err?.code,
              },
            });
          }
        },
      );

      unsubscribes.set(notificationUuId, unsubscribe);
    };

    notificationUuIds.forEach((notificationUuId) => {
      // Skip if already completed in this session
      if (completedUuIdsRef.current.has(notificationUuId)) {
        return;
      }
      subscribe(notificationUuId);
    });

    return () => {
      cancelled = true;
      unsubscribes.forEach((unsub) => unsub());
      unsubscribes.clear();
      timeouts.forEach((timeout) => clearTimeout(timeout));
      timeouts.clear();
      retryTimeouts.forEach((timeout) => clearTimeout(timeout));
      retryTimeouts.clear();
    };
    // Serialize the Set to a sorted string for stable dependency comparison
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Array.from(notificationUuIds).sort().join(','), uid]);
};
